// ============================================================================
// api_deviceIntercomm.ts
// Gestion des deux PeerConnections intercomm côté device :
//   - pcIntercommReceiveRef : régie → device  (recvonly audio)
//   - pcIntercommSendRef    : device → régie  (sendonly audio)
//
// Les deux PCs sont négociées à la connexion (offers envoyées par la régie).
// Le device répond avec des answers.
// ============================================================================

import type { MutableRefObject } from 'react';
import { defaultRTCConfig } from '../../dataTypes/liveStreamManagment/utils_webrtc';
import { SdpLogger } from '../../dataTypes/liveStreamManagment/types_callbacks';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface DeviceIntercommRefs {
  // Régie → Device : on reçoit l'audio de la régie
  pcIntercommReceiveRef    : MutableRefObject<RTCPeerConnection | null>;
  iceBufIntercommReceiveRef: MutableRefObject<RTCIceCandidateInit[]>;
  intercommReceiveStreamRef: MutableRefObject<MediaStream | null>;

  // Device → Régie : on envoie notre audio
  pcIntercommSendRef    : MutableRefObject<RTCPeerConnection | null>;
  iceBufIntercommSendRef: MutableRefObject<RTCIceCandidateInit[]>;
  intercommSendStreamRef: MutableRefObject<MediaStream | null>; // mic local capturé
}

export interface DeviceIntercommCallbacks {
  addSdpLog: SdpLogger;
  onIntercommReceiveReady: (stream: MediaStream) => void; // flux régie → device prêt
}

// ─── Helpers envoi WS ─────────────────────────────────────────────────────────

const wsSend = (wsRef: MutableRefObject<WebSocket | null>, payload: object): void => {
  if (wsRef.current?.readyState === WebSocket.OPEN)
    wsRef.current.send(JSON.stringify(payload));
};

// ============================================================================
// SECTION A — PC réception (régie → device)
// Créée à la réception de l'offer intercomm_regie_to_device
// ============================================================================

export const handleDeviceIntercommReceiveOffer = async (
  offer       : RTCSessionDescriptionInit,
  refs        : DeviceIntercommRefs,
  wsRef       : MutableRefObject<WebSocket | null>,
  callbacks   : DeviceIntercommCallbacks,
): Promise<void> => {
  // Fermer la PC précédente si elle existe
  if (refs.pcIntercommReceiveRef.current) {
    refs.pcIntercommReceiveRef.current.close();
    refs.pcIntercommReceiveRef.current = null;
  }
  refs.iceBufIntercommReceiveRef.current = [];

  callbacks.addSdpLog({
    direction: 'received', type: 'offer',
    label: '🎙 Intercomm receive offer ← régie',
    sdp: offer.sdp,
  });

  const pc = new RTCPeerConnection(defaultRTCConfig());
  refs.pcIntercommReceiveRef.current = pc;

  // Réception du track audio de la régie
  pc.ontrack = (event) => {
    const stream = event.streams[0] ?? new MediaStream([event.track]);
    callbacks.addSdpLog({
      direction: 'received', type: 'event',
      label: `🎙 Intercomm receive: track reçu (${event.track.kind}) streams=${event.streams.length}`,
    });
    refs.intercommReceiveStreamRef.current = stream;
    callbacks.onIntercommReceiveReady(stream);
  };

  pc.onicecandidate = (e) => {
    if (e.candidate) {
      wsSend(wsRef, {
        type     : 'ice-candidate',
        target   : 'control',
        stream_id: 'intercomm_regie_to_device',
        payload  : e.candidate,
      });
    }
  };

  pc.onconnectionstatechange = () => {
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `🎙 Intercomm receive PC: ${pc.connectionState}`,
    });
    if (pc.connectionState === 'connected') {
      // Log stats audio toutes les 5s pour vérifier que le son circule
      const statsInterval = setInterval(async () => {
        if (pc.connectionState !== 'connected') { clearInterval(statsInterval); return; }
        try {
          const stats = await pc.getStats();
          stats.forEach(report => {
            if (report.type === 'inbound-rtp' && report.kind === 'audio') {
              callbacks.addSdpLog({
                direction: 'info', type: 'event',
                label: `🎙📥 receive audioLevel=${(report.audioLevel ?? 0).toFixed(4)} totalSamples=${report.totalSamplesReceived ?? 0} packetsReceived=${report.packetsReceived ?? 0}`,
              });
            }
          });
        } catch {}
      }, 5000);
    }
    if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
      refs.pcIntercommReceiveRef.current = null;
    }
  };

  await pc.setRemoteDescription(offer);

  // Flush ICE buffer
  const buf = refs.iceBufIntercommReceiveRef.current.splice(0);
  for (const c of buf) {
    try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
  }

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  callbacks.addSdpLog({
    direction: 'sent', type: 'answer',
    label: '🎙 Intercomm receive answer → régie',
    sdp: answer.sdp,
  });

  wsSend(wsRef, {
    type     : 'answer',
    target   : 'control',
    stream_id: 'intercomm_regie_to_device',
    payload  : answer,
  });
};

// ============================================================================
// SECTION B — PC envoi (device → régie)
// Créée à la réception de l'offer intercomm_device_to_regie
// Le device répond avec un answer et ajoute son track audio
// ============================================================================

export const handleDeviceIntercommSendOffer = async (
  offer       : RTCSessionDescriptionInit,
  refs        : DeviceIntercommRefs,
  wsRef       : MutableRefObject<WebSocket | null>,
  callbacks   : DeviceIntercommCallbacks,
): Promise<void> => {
  if (refs.pcIntercommSendRef.current) {
    refs.pcIntercommSendRef.current.close();
    refs.pcIntercommSendRef.current = null;
  }
  refs.iceBufIntercommSendRef.current = [];

  callbacks.addSdpLog({
    direction: 'received', type: 'offer',
    label: '🎙 Intercomm send offer ← régie',
    sdp: offer.sdp,
  });

  const pc = new RTCPeerConnection(defaultRTCConfig());
  refs.pcIntercommSendRef.current = pc;

  // Ajouter le track audio muet si on a déjà un stream mic
  if (refs.intercommSendStreamRef.current) {
    const audioTrack = refs.intercommSendStreamRef.current.getAudioTracks()[0];
    if (audioTrack) {
      // Muet par défaut — activé uniquement quand l'utilisateur appuie
      audioTrack.enabled = false;
      pc.addTrack(audioTrack, refs.intercommSendStreamRef.current);
    }
  }

  pc.onicecandidate = (e) => {
    if (e.candidate) {
      wsSend(wsRef, {
        type     : 'ice-candidate',
        target   : 'control',
        stream_id: 'intercomm_device_to_regie',
        payload  : e.candidate,
      });
    }
  };

  pc.onconnectionstatechange = () => {
    callbacks.addSdpLog({
      direction: 'info', type: 'event',
      label: `🎙 Intercomm send PC: ${pc.connectionState}`,
    });
    if (pc.connectionState === 'connected') {
      // Log stats audio toutes les 5s pour vérifier que le son circule
      const statsInterval = setInterval(async () => {
        if (pc.connectionState !== 'connected') { clearInterval(statsInterval); return; }
        try {
          const stats = await pc.getStats();
          stats.forEach(report => {
            if (report.type === 'outbound-rtp' && report.kind === 'audio') {
              const trackEnabled = refs.intercommSendStreamRef.current?.getAudioTracks()[0]?.enabled ?? false;
              callbacks.addSdpLog({
                direction: 'info', type: 'event',
                label: `🎙📤 send packetsSent=${report.packetsSent ?? 0} bytesSent=${report.bytesSent ?? 0} trackEnabled=${trackEnabled}`,
              });
            }
          });
        } catch {}
      }, 5000);
    }
    if (pc.connectionState === 'failed' || pc.connectionState === 'closed') {
      refs.pcIntercommSendRef.current = null;
    }
  };

  await pc.setRemoteDescription(offer);

  // Flush ICE buffer
  const buf = refs.iceBufIntercommSendRef.current.splice(0);
  for (const c of buf) {
    try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch {}
  }

  const answer = await pc.createAnswer();
  await pc.setLocalDescription(answer);

  callbacks.addSdpLog({
    direction: 'sent', type: 'answer',
    label: '🎙 Intercomm send answer → régie',
    sdp: answer.sdp,
  });

  wsSend(wsRef, {
    type     : 'answer',
    target   : 'control',
    stream_id: 'intercomm_device_to_regie',
    payload  : answer,
  });
};

// ============================================================================
// SECTION C — ICE candidates entrants
// ============================================================================

export const handleDeviceIntercommReceiveIce = async (
  candidate: RTCIceCandidateInit,
  refs     : Pick<DeviceIntercommRefs, 'pcIntercommReceiveRef' | 'iceBufIntercommReceiveRef'>,
): Promise<void> => {
  if (!refs.pcIntercommReceiveRef.current) {
    refs.iceBufIntercommReceiveRef.current.push(candidate);
    return;
  }
  try { await refs.pcIntercommReceiveRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
};

export const handleDeviceIntercommSendIce = async (
  candidate: RTCIceCandidateInit,
  refs     : Pick<DeviceIntercommRefs, 'pcIntercommSendRef' | 'iceBufIntercommSendRef'>,
): Promise<void> => {
  if (!refs.pcIntercommSendRef.current) {
    refs.iceBufIntercommSendRef.current.push(candidate);
    return;
  }
  try { await refs.pcIntercommSendRef.current.addIceCandidate(new RTCIceCandidate(candidate)); } catch {}
};

// ============================================================================
// SECTION D — Activation / désactivation micro intercomm (device → régie)
// ============================================================================

/**
 * Active ou désactive le track audio envoyé vers la régie.
 * Envoie aussi un message WS pour que la régie affiche le halo vert.
 */
export const setDeviceIntercommSpeaking = (
  active : boolean,
  refs   : Pick<DeviceIntercommRefs, 'pcIntercommSendRef' | 'intercommSendStreamRef'>,
  wsRef  : MutableRefObject<WebSocket | null>,
): void => {
  // Activer/désactiver le track audio
  if (refs.intercommSendStreamRef.current) {
    refs.intercommSendStreamRef.current.getAudioTracks().forEach(t => {
      t.enabled = active;
    });
  }

  // Notifier la régie
  wsSend(wsRef, {
    type   : 'intercomm_speaking',
    target : 'control',
    active,
  });
};

// ============================================================================
// SECTION E — Fermeture
// ============================================================================

export const closeDeviceIntercomm = (
  refs     : DeviceIntercommRefs,
  callbacks: Pick<DeviceIntercommCallbacks, 'addSdpLog'>,
): void => {
  if (refs.pcIntercommReceiveRef.current) {
    refs.pcIntercommReceiveRef.current.close();
    refs.pcIntercommReceiveRef.current = null;
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm receive PC fermée' });
  }
  if (refs.pcIntercommSendRef.current) {
    refs.pcIntercommSendRef.current.close();
    refs.pcIntercommSendRef.current = null;
    callbacks.addSdpLog({ direction: 'info', type: 'event', label: '🎙 Intercomm send PC fermée' });
  }
  refs.iceBufIntercommReceiveRef.current = [];
  refs.iceBufIntercommSendRef.current    = [];
  refs.intercommReceiveStreamRef.current = null;
  // Ne pas libérer intercommSendStreamRef ici — géré par LiveStreamManager
};